import { useEffect, useState } from "react";
import { PointOfInterest } from "../../../../generated/gpb/api/route";
import { RouteId } from "../../../../generated/gpb/api/id";
import { useLazyGetPointsOfInterestListQuery } from "../../../../services/routing";
import { useAppDispatch } from "../../../../app/hooks";
import {
    OpenRoadsStatus,
    setOpenRoadsQueryFinished,
    setOpenRoadStopsPresent,
    useOpenRoadsStatus
} from "../../../../features/mapDisplay/mapDisplaySlice";

export function useOpenRoadsPoints( routeId: RouteId, totalPoints: number | undefined )
{
    const dispatch = useAppDispatch();
    const openRoadsLoaded = useOpenRoadsStatus();
    const [ triggerGetPOI ] = useLazyGetPointsOfInterestListQuery();
    const [ points, setPoints ] = useState<Array<PointOfInterest>>( [] );
    const [ loading, setLoading ] = useState( true );

    useEffect( () =>
    {
        if ( totalPoints === undefined || totalPoints <= 0 ) return;

        let isCancelled = false;

        const fetchPoints = async () =>
        {
            const fetchPOI = async ( index: number, count: number ) =>
            {
                try
                {
                    const result = await triggerGetPOI( {
                        routeId: routeId.hexString,
                        index,
                        count,
                    } ).unwrap();
                    return result?.pointsOfInterestList ?? [];
                }
                catch ( e )
                {
                    console.error( "Error fetching POIs:", e );
                    return [];
                }
            };

            const fetchInPages = async ( pageSize: number ) =>
            {
                const pages = Math.ceil( totalPoints / pageSize );
                const promises = Array.from( { length: pages }, ( _, i ) => fetchPOI( i * pageSize, Math.min( pageSize, totalPoints - i * pageSize ) ) );
                const results = await Promise.all( promises );
                return results.flat();
            };

            setLoading( true );
            if ( openRoadsLoaded !== OpenRoadsStatus.Loading ) return;

            try
            {
                const result = await fetchPOI( 0, totalPoints );
                if ( result.length > 0 && !isCancelled )
                {
                    setPoints( result );
                    dispatch( setOpenRoadStopsPresent() );
                    setLoading( false );
                    return;
                }

                const fallback = await fetchInPages( 10000 );
                if ( fallback.length > 0 && !isCancelled )
                {
                    setPoints( fallback );
                    dispatch( setOpenRoadStopsPresent() );
                    setLoading( false );
                    return;
                }

                const fallback2 = await fetchInPages( 5000 );
                if ( !isCancelled )
                {
                    setPoints( fallback2 );
                    if ( fallback2.length > 0 )
                    {
                        dispatch( setOpenRoadStopsPresent() );
                    }
                    else
                    {
                        dispatch( setOpenRoadsQueryFinished() );
                    }
                    setLoading( false );
                }
            }
            catch ( e )
            {
                console.error( "Error loading POIs", e );
                if ( !isCancelled ) setLoading( false );
            }
        };

        void fetchPoints();

        return () =>
        {
            isCancelled = true;
        };
    }, [ routeId, totalPoints ] );

    return { points, loading };
}
